const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://neoserve-business-api.royal-brook-d1c1.workers.dev'
const INFERENCE_BASE = import.meta.env.VITE_INFERENCE_API_URL || 'http://localhost:8080'

export interface APIKey {
  id: string
  name: string
  prefix: string
  status: 'active' | 'revoked'
  rate_limit_rpm: number
  last_used_at?: string
  created_at: string
}

export interface CreateAPIKeyResponse {
  key: string
  prefix: string
  id: string
  name: string
  created_at: string
}

export interface CompletionMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface ModelInfo {
  id: string
  name: string
  tier: 'speed' | 'standard' | 'premium' | 'media'
  description: string
  context_window: number
  input_cost_per_1m: number
  output_cost_per_1m: number
  supports_streaming: boolean
  supports_vision: boolean
  speed_ms?: number
}

// Neoserve model catalog
export const CLOUDMIND_MODELS: ModelInfo[] = [
  {
    id: 'cm-flash',
    name: 'CM Flash',
    tier: 'speed',
    description: 'Ultra-fast responses for simple tasks, chatbots, and high-volume applications.',
    context_window: 32768,
    input_cost_per_1m: 0.05,
    output_cost_per_1m: 0.10,
    supports_streaming: true,
    supports_vision: false,
    speed_ms: 180,
  },
  {
    id: 'cm-speed',
    name: 'CM Speed',
    tier: 'speed',
    description: 'Balanced speed and capability for everyday AI tasks at minimal cost.',
    context_window: 128000,
    input_cost_per_1m: 0.10,
    output_cost_per_1m: 0.20,
    supports_streaming: true,
    supports_vision: false,
    speed_ms: 350,
  },
  {
    id: 'cm-standard',
    name: 'CM Standard',
    tier: 'standard',
    description: 'General-purpose model for content generation, summarization, and analysis.',
    context_window: 128000,
    input_cost_per_1m: 0.30,
    output_cost_per_1m: 0.60,
    supports_streaming: true,
    supports_vision: false,
    speed_ms: 800,
  },
  {
    id: 'cm-standard-vision',
    name: 'CM Standard Vision',
    tier: 'standard',
    description: 'Multimodal model supporting text and image inputs for document and image analysis.',
    context_window: 128000,
    input_cost_per_1m: 0.50,
    output_cost_per_1m: 1.00,
    supports_streaming: true,
    supports_vision: true,
    speed_ms: 1100,
  },
  {
    id: 'cm-pro',
    name: 'CM Pro',
    tier: 'premium',
    description: 'Advanced reasoning and complex problem-solving for enterprise workloads.',
    context_window: 200000,
    input_cost_per_1m: 2.00,
    output_cost_per_1m: 8.00,
    supports_streaming: true,
    supports_vision: true,
    speed_ms: 2500,
  },
  {
    id: 'cm-pro-deep',
    name: 'CM Pro Deep',
    tier: 'premium',
    description: 'Extended thinking mode for research, complex analysis, and multi-step reasoning.',
    context_window: 200000,
    input_cost_per_1m: 5.00,
    output_cost_per_1m: 20.00,
    supports_streaming: true,
    supports_vision: true,
    speed_ms: 8000,
  },
  {
    id: 'cm-image-gen',
    name: 'CM Image Gen',
    tier: 'media',
    description: 'High-quality image generation from text prompts.',
    context_window: 4096,
    input_cost_per_1m: 0,
    output_cost_per_1m: 0,
    supports_streaming: false,
    supports_vision: true,
    speed_ms: 5000,
  },
  {
    id: 'cm-audio-transcribe',
    name: 'CM Transcribe',
    tier: 'media',
    description: 'Fast and accurate audio transcription and translation.',
    context_window: 0,
    input_cost_per_1m: 0,
    output_cost_per_1m: 0,
    supports_streaming: true,
    supports_vision: false,
    speed_ms: 2000,
  },
]

async function fetchWithAuth(url: string, token: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  return response
}

export async function createAPIKey(
  name: string,
  token: string
): Promise<CreateAPIKeyResponse> {
  const response = await fetchWithAuth('/api/keys', token, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
  return response.json()
}

export async function listAPIKeys(token: string): Promise<APIKey[]> {
  const response = await fetchWithAuth('/api/keys', token)
  const data = await response.json()
  return data.keys || []
}

export async function revokeAPIKey(keyId: string, token: string): Promise<void> {
  await fetchWithAuth(`/api/keys/${keyId}`, token, {
    method: 'DELETE',
  })
}

export async function testCompletion(
  model: string,
  messages: CompletionMessage[],
  apiKey: string
): Promise<string> {
  const response = await fetch(`${INFERENCE_BASE}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      messages,
      stream: false,
    }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  const data = await response.json()
  return data.choices?.[0]?.message?.content || ''
}

export async function streamCompletion(
  model: string,
  messages: CompletionMessage[],
  apiKey: string,
  onChunk: (chunk: string) => void,
  onDone: (usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number }) => void
): Promise<void> {
  const response = await fetch(`${INFERENCE_BASE}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      messages,
      stream: true,
    }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let usage = { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    const chunk = decoder.decode(value)
    const lines = chunk.split('\n').filter(line => line.startsWith('data: '))

    for (const line of lines) {
      const data = line.slice(6)
      if (data === '[DONE]') {
        onDone(usage)
        return
      }

      try {
        const parsed = JSON.parse(data)
        const content = parsed.choices?.[0]?.delta?.content
        if (content) onChunk(content)
        if (parsed.usage) usage = parsed.usage
      } catch {
        // Skip malformed chunks
      }
    }
  }

  onDone(usage)
}
